<template lang="pug">

  v-app.resetPasswordContainer
    confirm-dialog(ref="cdConfirmModal" :title="cdParameters.title" :text="cdParameters.text" :buttonPosition="cdParameters.buttonPosition" :width="cdParameters.width" @close="cdMessageResponse")

    .sos-login-card
      .sos-login-card__heading
        .sos-login-card__headingInner
          .sosAuthCardHeadingLogo(:style="{ backgroundImage: 'url(' + logo + ')' }")

      .sos-login-card__inner

        template(v-if="status === 'LOADING'")
          .sos-login-card__tabBody
            .resetPassword__stateBlock
              v-progress-circular.mb-4(indeterminate size="40" color="#e54245")
              .resetPassword__stateTitle {{ loadingTitle }}
              .resetPassword__stateSubtitle Por favor, aguarde um momento.

        template(v-else-if="status === 'DONE'")
          .sos-login-card__tabBody
            form(action="" @submit.prevent="submitNewPassword")
              p.cardWelcomeTitle.mt-6.mb-1 Crie uma nova senha
              p.resetPassword__emailLabel.mb-5 {{ userEmail }}

              .resetPassword__fields

                div
                  sos-login-field(
                    ref="newPassword"
                    name="nova-senha"
                    :type="newPassword.hidden ? 'password' : 'text'"
                    v-model="newPassword.value"
                    :maxLength="64"
                    placeholder="Nova senha"
                    :showSuccess="newPassword.success"
                    :showError="newPassword.error"
                    :fieldErrors="newPassword.errorMessages"
                    :disabled="loading"
                    :showButton="true"
                    @buttonClick="newPassword.hidden = !newPassword.hidden"
                    :buttonIcon="newPassword.hidden ? 'mdi-eye' : 'mdi-eye-off'"
                    :buttonDisabled="loading"
                  )

                div
                  sos-login-field(
                    ref="confirmPassword"
                    name="confirmar-senha"
                    :type="confirmPassword.hidden ? 'password' : 'text'"
                    v-model="confirmPassword.value"
                    :maxLength="64"
                    placeholder="Confirme a nova senha"
                    :showSuccess="confirmPassword.success"
                    :showError="confirmPassword.error"
                    :fieldErrors="confirmPassword.errorMessages"
                    :disabled="loading"
                    :showButton="true"
                    @buttonClick="confirmPassword.hidden = !confirmPassword.hidden"
                    :buttonIcon="confirmPassword.hidden ? 'mdi-eye' : 'mdi-eye-off'"
                    :buttonDisabled="loading"
                  )

                div
                  sos-button(
                    type="submit"
                    size="big"
                    buttonLabel="Salvar nova senha"
                    buttonIconSize="20"
                    :disabled="loading"
                    :loading="loading"
                    full
                  )

        template(v-else-if="status === 'ERROR'")
          .sos-login-card__tabBody
            .resetPassword__stateBlock
              v-icon.mb-4(size="48" color="#e54245") mdi-alert-circle-outline
              .resetPassword__stateTitle Não foi possível processar.
              .resetPassword__stateSubtitle {{ errorMessage }}

</template>

<script>
import logo from "@/assets/logo.png";
import fieldManagement from "@/constants/loginFieldObject";
import confirmDialogMixin from "@/mixins/cd.mixin.js";

import SosButton from "@/components/SosButton";
import SosLoginField from "@/components/SosLoginField";
import ConfirmDialog from "@/modals/ConfirmDialog";
import {
  apiVerifyPasswordResetToken,
  apiSetNewPassword,
} from "@/services/endpoints/session";

export default {
  name: "ResetPassword",

  components: {
    "sos-button": SosButton,
    "sos-login-field": SosLoginField,
    "confirm-dialog": ConfirmDialog,
  },

  mixins: [confirmDialogMixin],

  data() {
    return {
      logo,
      status: "LOADING",
      loadingTitle: "Verificando link...",
      loading: false,
      userEmail: "",
      errorMessage: "O link pode ser inválido ou ter expirado.",
      newPassword: { ...fieldManagement, value: "", hidden: true },
      confirmPassword: { ...fieldManagement, value: "", hidden: true },
    };
  },

  computed: {
    token() {
      return this.$route.params.token;
    },
  },

  async created() {
    const apiPromise = apiVerifyPasswordResetToken(this.token).catch(
      () => null
    );

    await new Promise((resolve) => setTimeout(resolve, 2000));

    this.loadingTitle = "Tentando finalizar Sessões...";

    const [response] = await Promise.all([
      apiPromise,
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]).catch(() => [null]);

    if (response?.httpCode === 200) {
      this.userEmail = response.data?.email ?? "";
      this.status = "DONE";
    } else {
      this.errorMessage =
        response?.message || "O link pode ser inválido ou ter expirado.";
      this.status = "ERROR";
    }
  },

  methods: {
    cdMessageResponse() {
      if (this._redirectAfterClose) {
        this._redirectAfterClose = false;
        this.$router.push("/login");
      }
    },

    async submitNewPassword() {
      this.newPassword.setError("");
      this.confirmPassword.setError("");

      if (this.newPassword.value.length < 6) {
        this.newPassword.setError("A senha deve ter no mínimo 6 caracteres.");
        this.$nextTick(() => {
          this.$refs.newPassword.triggerBounce();
        });
      }

      if (this.confirmPassword.value !== this.newPassword.value) {
        this.confirmPassword.setError("As senhas não coincidem.");
        this.$nextTick(() => {
          this.$refs.confirmPassword.triggerBounce();
        });
      }

      if (this.newPassword.error || this.confirmPassword.error) return;

      this.loading = true;

      try {
        const response = await apiSetNewPassword(
          this.token,
          this.newPassword.value
        );

        if (response.httpCode === 200) {
          this._redirectAfterClose = true;
          this.showMessage(response.title, response.message);
        } else {
          this.showMessage(response.title, response.message);
        }
      } catch (e) {
        this.showMessage(
          "Atenção!",
          "Ocorreu um erro inesperado. Tente novamente."
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resetPasswordContainer {
  max-width: 900px;
  width: 100%;
  height: 100vh;
  padding: 12px;
  margin-right: auto;
  margin-left: auto;

  ::v-deep .v-application--wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
  }
}

.resetPassword__fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 12px 20px 16px 20px;
}

.resetPassword__emailLabel {
  font-size: 13px;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.01em;
}

.resetPassword__stateBlock {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.resetPassword__stateTitle {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 8px;
}

.resetPassword__stateSubtitle {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.5;
}
</style>
